import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useMerchantCurrency } from '@bringg-frontend/hooks';

import { PLANNED_DELIVERY_WINDOWS_MAX_NAMES_BEFORE_COUNT } from '../../../constants';
import { PlannedDeliveryWindowsEventType } from '../../planned-delivery-windows-calendar';
import { useStores } from '../../../../../recipes';

import styles from './planned-delivery-windows-event.module.scss';

interface NamesLineProps {
	ids: number[] | undefined;
	getName: (id: number) => string | undefined;
	labelTranslationKey: string;
	countTranslationKey: string;
}

const NamesLine = observer(({ ids, getName, labelTranslationKey, countTranslationKey }: NamesLineProps) => {
	const { t } = useTranslation();

	if (!ids?.length) {
		return null;
	}

	const count = ids.length;

	if (count > PLANNED_DELIVERY_WINDOWS_MAX_NAMES_BEFORE_COUNT) {
		return <span className={styles.item}>{t(countTranslationKey, { count })}</span>;
	}

	const names = ids.map(getName).filter(Boolean);
	if (!names.length) {
		return null;
	}

	return (
		<span className={styles.item}>{t(labelTranslationKey, { count: names.length, names: names.join(', ') })}</span>
	);
});

interface FeeLineProps {
	fee: number | null | undefined;
	currencySymbol: string;
}

const FeeLine = ({ fee, currencySymbol }: FeeLineProps) => {
	const { t } = useTranslation();

	if (!fee) {
		return null;
	}

	return (
		<span className={styles.item}>
			{t('PLANNED_DELIVERY_WINDOWS_SLOT_CARD.FEE_LABEL', { value: `${currencySymbol}${fee.toFixed(2)}` })}
		</span>
	);
};

interface Props {
	event: PlannedDeliveryWindowsEventType;
}

const PlannedDeliveryWindowsEventRelationsName = ({ event }: Props) => {
	const { servicePlansStore, serviceArea, merchantConfigurationsStore } = useStores();
	const { symbol: currencySymbol } = useMerchantCurrency();
	const manageFeeEnabled = merchantConfigurationsStore.configuration?.manage_delivery_windows_fee;

	return (
		<>
			<NamesLine
				ids={event.service_plan_ids}
				getName={id => servicePlansStore.servicePlans.get(id)?.name}
				labelTranslationKey="PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_PLAN_LABEL"
				countTranslationKey="PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_PLANS_COUNT"
			/>
			<NamesLine
				ids={event.service_area_ids}
				getName={id => serviceArea.get(id)?.name}
				labelTranslationKey="PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_AREA_LABEL"
				countTranslationKey="PLANNED_DELIVERY_WINDOWS_SLOT_CARD.SERVICE_AREAS_COUNT"
			/>
			{manageFeeEnabled && <FeeLine fee={event.fee} currencySymbol={currencySymbol} />}
		</>
	);
};

export default observer(PlannedDeliveryWindowsEventRelationsName);
